import { Inject, Injectable } from '@angular/core';
import { TimezoneService } from '../../../services/date/timezone.service';
import { DateService } from '../../../services/date/date.service';
import { DateFormatterService, DateTimeFormat } from '../../../services/date/date-formatter.service';
import { DateParserService } from '../../../services/date/date-parser.service';
import { LocaleService } from '../../../services/locale/locale.service';

import type { DatePickerFormatType } from '../../../services/date/date-formatter.service';
import type { LocaleDetails } from '../../../services/locale/locale.domain';
import type { DateValidator } from './date-validator.type';

interface Format {
  pattern: string;
  parseDate(value: string): Date;
}

/**
 * The Date Picker Service
 */
@Injectable()
export class DatePickerService {
  /**
   * List of supported formats
   */
  private SUPPORTED_FORMATS: Map<DatePickerFormatType, Format> = new Map<DatePickerFormatType, Format>();

  private localeDetails: LocaleDetails;
  /**
   * The constructor of the component.
   * Configures list of supported formats
   *
   * @param timezoneService
   * @param dateFormatterService
   * @param dateParserService
   * @param localeService
   */
  constructor(@Inject(TimezoneService) private timezoneService: TimezoneService,
              @Inject(DateFormatterService) private dateFormatterService: DateFormatterService,
              @Inject(DateParserService) private dateParserService: DateParserService,
              @Inject(LocaleService) private localeService: LocaleService) {
    this.localeDetails = this.localeService.getLocaleDetails();
    const monthsPattern = this.localeDetails.calendarStrings.months.join('|');
    this.SUPPORTED_FORMATS.set(DateTimeFormat.YEAR_MONTH_DAY, {
      pattern: '\\d{4}-\\d{2}-\\d{2}',
      parseDate: (value: string): Date => this.dateParserService.parseDate(value)
    });
    this.SUPPORTED_FORMATS.set(DateTimeFormat.MONTH_DAY, {
      pattern: '\\d{4}',
      parseDate: (value: string): Date =>
        this.dateParserService.parseMonthDayToDate(value, this.timezoneService.getCurrentDateInServerTimezone())
    });
    this.SUPPORTED_FORMATS.set(DateTimeFormat.MONTH_YEAR, {
      pattern: '\\d{2}-\\d{4}',
      parseDate: (value: string): Date => this.dateParserService.parseMonthYearToDate(value)
    });
    this.SUPPORTED_FORMATS.set(DateTimeFormat.MONTH_DAY_YEAR, {
      pattern: `^(${monthsPattern})\\s\\d{1,2},\\s\\d{4}$`,
      parseDate: (value: string): Date => this.dateParserService.parseLongMonthDayYearToDate(value)
    });
  }

  /**
   * Get date pattern of format.
   *
   * @param format The date format
   * @returns If format is invalid, returns DEFAULT_DATE_FORMAT. Otherwise, returns format.
   */
  getDateFormat(format: DatePickerFormatType | undefined): DatePickerFormatType {
    return (format === undefined || format === null) ? DateTimeFormat.YEAR_MONTH_DAY : format;
  }

  /**
   * Get date pattern of format.
   *
   * @param format The date format
   * @returns If argument is valid format, returns argument. Otherwise returns DEFAULT_DATE_FORMAT
   */
  getDatePattern(format: DatePickerFormatType): string {
    return this.SUPPORTED_FORMATS.get(this.getDateFormat(format)).pattern;
  }

  getPlaceholderFormat(format: DatePickerFormatType): string {
    return this.dateFormatterService.getLocalizedFormat(format);
  }

  /**
   * Parse string to Date Object of format.
   *
   * @param value Value to parse to Date object
   * @param format The date format
   * @returns Date object parsed from value
   */
  parseDate(value: string, format: DatePickerFormatType): Date {
    return this.SUPPORTED_FORMATS.get(this.getDateFormat(format)).parseDate(value);
  }

  /**
   * Delegates to DateUtilityService.isDate
   *
   * @param date
   * @returns
   */
  isDate(date: unknown): boolean {
    return DateService.isDate(date);
  }

  /**
   * Function to test if a date is disabled
   *
   * @param date Date to check if it's disabled
   * @param disableDates Function to test if date is disabled
   * @param disabledDateValidators Array of functions to test if date is disabled
   * @returns Returns true if date is disabled
   */
  isDisabledDate(date: Date, disableDates: DateValidator, disabledDateValidators?: Array<DateValidator>): boolean {
    let result = false;
    DateService.stripTime(date);
    result = disableDates === undefined ? result : disableDates(date);
    result = (!result && disabledDateValidators !== undefined) ? !this._validateDate(date, disabledDateValidators) : result;
    return result;
  }

  /**
   * Formats date from Date object
   *
   * @param date Date object to format
   * @param format Date format
   * @returns Returns formatted date
   */
  formatDate(date: Date, format: DatePickerFormatType): string {
    return this.dateFormatterService.formatDate(date, this.getDateFormat(format));
  }

  /**
   * Validation function for minDate.
   *
   * @param minDate Minimum date
   * @returns Returns minDate validation function
   */
  validateMinDate(minDate: Date): DateValidator {
    return ((date: Date): boolean => minDate === undefined || date === undefined || date < minDate);
  }

  /**
   * Validation function for maxDate.
   *
   * @param maxDate Maximum date
   * @returns Returns maxDate validation function
   */
  validateMaxDate(maxDate: Date): DateValidator {
    return ((date: Date): boolean => maxDate === undefined || date === undefined || date > maxDate);
  }

  /**
   * Validation function for no weekends.
   *
   * @returns Returns no weekends validation function
   */
  validateNoWeekends(): DateValidator {
    return ((date: Date): boolean => !DateService.isDate(date) || (date.getDay() !== 0 && date.getDay() !== 6));
  }

  /**
   * Validation function for no weekends.
   *
   * @param format Format of date pattern
   * @param date Formatted date pattern
   * @returns Returns true if date has valid pattern
   */
  validateDatePattern(format: DatePickerFormatType, date: string): boolean {
    const regExp = new RegExp(this.getDatePattern(format));
    return regExp.exec(date) !== null;
  }

  /**
   * This function checks if a date is valid from list of validators
   *
   * @param date The date to validate
   * @param validators Array of validator functions
   * @returns Returns true if date is valid
   */
  private _validateDate(date: Date, validators: Array<DateValidator>): boolean {
    let result = true;
    let i: number;
    let validator: unknown;
    for (i = 0; result && i < validators.length; i++) {
      validator = validators[i];
      if (typeof validator === 'function') {
        result = validator(date);
      }
    }
    return result;
  }

}
